import './App.css';
import './styles/components.css';
import Header from './components/Header';
import StatusBar from './components/Stats/StatusBar';
import FleetVisualization from './components/Fleet/FleetVisualization';
import FleetPanel from './components/Fleet/FleetPanel';
import PlayerNameModal from './components/PlayerNameModal';
import DebugPanel from './components/DebugPanel';
import LeaderboardPanel from './components/Leaderboard/LeaderboardPanel';
import ConnectionOverlay from './components/ConnectionStatus/ConnectionOverlay';
import { debugLog } from './utils/debugLogger'
import { DangerAlert } from './components/DangerPanel';
import ReturnPanel, { StatsReturnPanel } from './components/ReturnPanel';
import AdminTools from './components/Admin/AdminTools';
import AdminPage from './components/Admin/AdminPage'; // Added AdminPage import
import ImportGameState from './components/ImportGameState';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useGameState } from './state/useGameState';
import { GameStateProvider } from './state/GameStateContext';
import NetworkErrorDisplay from './components/NetworkErrorDisplay';
import { useState, useEffect } from 'react';
import { useSignalR } from './context/SignalRContext';

// Get base path from import.meta.env
const BASE_URL = import.meta.env.BASE_URL || '/';
debugLog('[App] Using base URL:', BASE_URL);

function App() {
  const { isConnected } = useSignalR();
  const gameStateApi = useGameState();
  const {
    playerId,
    showModal,
    loading,
    gameState,
    debugMode,
    setDebugMode,
    pollInterval,
    setPollInterval,
    handlePlayerNameSubmit,
    basicOre,
    fleet,
    doWarpFleet,
    doRecallFleet,
    doWarpShip,
    doRecallShip,
    doPurchaseUpgrade,
    loadGameState,
    showReturnModal,
    absenceData,
    handleReturnModalClose,
  } = gameStateApi;
  const [hasConnectedOnce, setHasConnectedOnce] = useState(false);
  // State for showing panels
  const [showAlert] = useState(false);
  const [showStatsPanel, setShowStatsPanel] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  useEffect(() => {
    if (isConnected) {
      setHasConnectedOnce(true);
    }
  }, [isConnected]);


  // Helper function to format time duration
  const formatTimeDuration = (ms: number) => {
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / 1000 / 60) % 60);
    const hours = Math.floor((ms / 1000 / 60 / 60));
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes}m ${seconds}s`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Helper function to format large numbers with SI suffixes
  const formatNumberWithSuffix = (num: number): string => {
    if (num === 0) return '0';

    const suffixes = ['', 'K', 'M', 'B', 'T'];
    const magnitude = Math.floor(Math.log10(num) / 3);
    const scaledNum = num / Math.pow(10, magnitude * 3);

    if (magnitude === 0) return num.toLocaleString();
    return scaledNum.toFixed(2) + suffixes[magnitude];
  };

  // Calculate total yield per second from all ships in the fleet
  const totalYieldPerSecond = Array.isArray(fleet)
    ? fleet.reduce((sum, ship) => sum + (typeof ship.effectiveMiningYield === 'number' ? ship.effectiveMiningYield : 0), 0)
    : 0;

  // Rate of mining while away (ore per second)
  const absenceMiningRate = absenceData.absenceTime > 0 ?
    (absenceData.oreMined / (absenceData.absenceTime / 1000)).toFixed(1) : '0';
  // Prepare return panel fields with more detailed information
  const returnPanelFields = [
    {
      label: 'Time Away',
      value: formatTimeDuration(absenceData.absenceTime)
    },
    {
      label: 'Ore Mined While Away',
      value: `${formatNumberWithSuffix(absenceData.oreMined)} (${absenceMiningRate}/s)`
    },
    {
      label: 'Trips to Mothership',
      value: absenceData.tripsCompleted > 0
        ? `${absenceData.tripsCompleted.toLocaleString()} trips completed`
        : 'No trips completed'
    }
  ];
  let connectionMessage: string | null = null;
  if (!isConnected) {
    connectionMessage = hasConnectedOnce
      ? 'Connection lost. Attempting to reconnect...'
      : 'Establishing connection to Fleet Command...';
  } else if (!gameState && loading) {
    connectionMessage = 'Synchronizing your fleet telemetry...';
  }
  const showConnectionOverlay = connectionMessage !== null;

  return (
    <GameStateProvider value={gameStateApi}>
    <BrowserRouter basename={BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL}>
      <Routes>
        <Route path="/import" element={<ImportGameState />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route
          path="/"
          element={
            <div className="app-container">
              <Header
                onToggleDebug={() => setDebugMode((d) => !d)}
                onShowStats={() => setShowStatsPanel(true)}
                onShowLeaderboard={() => setShowLeaderboard(true)}
                lastUpdated={gameState?.lastUpdated}
                pollInterval={pollInterval}
                loading={loading}
              />

              {debugMode && <div style={{ marginTop: '.5rem' }}>
                <DebugPanel playerId={playerId} lastUpdated={gameState?.lastUpdated} pollInterval={pollInterval} setPollInterval={setPollInterval} />
              </div>
              }

              <div className="main-content">
                <div style={{ marginTop: '.5rem' }}>
                  <FleetVisualization
                    fleet={fleet}
                    currentMiningZoneId={gameState?.activeMiningZone?.id || 'ore belt'}
                    pirateAttackActive={gameState?.pirateAttackActive}
                  />
                </div>

                <div style={{ marginTop: '.5rem' }}>
                  <StatusBar totalYieldPerSecond={totalYieldPerSecond} basicOre={basicOre} prestigeStatus={gameState?.prestigeStatus} />
                </div>              <div style={{ marginTop: '.5rem' }}>
                  <FleetPanel
                    fleet={fleet}
                    playerId={playerId}
                    reload={loadGameState}
                    onWarpAll={doWarpFleet}
                    onRecallAll={doRecallFleet}
                    onWarpShip={doWarpShip}
                    onRecallShip={doRecallShip}
                    onPurchaseUpgrade={doPurchaseUpgrade}
                    basicOre={basicOre}
                  />
                </div>            </div>

              {/* Modals and panels */}
              {showModal && <PlayerNameModal onSubmit={handlePlayerNameSubmit} />}
              {showAlert && <DangerAlert />}
              {showReturnModal && <ReturnPanel fields={returnPanelFields} title="WELCOME BACK" onClose={handleReturnModalClose} />}
              {showStatsPanel && <StatsReturnPanel gameState={gameState} onClose={() => setShowStatsPanel(false)} />}
              {showLeaderboard && <LeaderboardPanel playerId={playerId} onClose={() => setShowLeaderboard(false)} />}
              <AdminTools playerId={playerId} />
              <NetworkErrorDisplay />
              {showConnectionOverlay && (
                <ConnectionOverlay
                  message={connectionMessage || 'Establishing connection...'}
                  subtle={hasConnectedOnce && !isConnected}
                />
              )}
            </div>
          } />
      </Routes>
    </BrowserRouter>
    </GameStateProvider>
  );
}

export default App;
